import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSession } from '@/stores/session';
import { Icon } from '@/components/Icon';
import { installFromBrowserPrompt } from '@/shell/installPrompt';
import s from './InstallGuidePage.module.css';

const SNOOZE_KEY = 'ims.v2.install-snooze-until';

export function InstallGuidePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo === '/me/notifications' ? '/me/notifications' : '/me';
  const user = useSession((state) => state.user);
  const [installStatus, setInstallStatus] = useState('');
  const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const safari = /Safari/i.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/i.test(navigator.userAgent);
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const secure = window.isSecureContext;
  const android = /Android/i.test(navigator.userAgent);
  const dismiss = (snooze: boolean) => {
    if (snooze) { try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000)); } catch { /* storage may be unavailable */ } }
    navigate(returnTo);
  };
  const install = async () => {
    try {
      const result = await installFromBrowserPrompt();
      setInstallStatus(result === 'accepted' ? '已安装到桌面' : result === 'dismissed' ? '已取消安装' : '请打开 Chrome 菜单，选择「安装应用」或「添加到主屏幕」');
    } catch { setInstallStatus('安装失败，请从浏览器菜单重试'); }
  };

  return <div className={s.page}>
    <div className={s.backdrop} />
    <section className={`${s.panel} ${ios && safari ? s.iosPanel : ''}`} role="dialog" aria-modal="true" aria-label="添加到主屏幕">
      {android ? <div className={`${s.title} ${s.androidTitle}`}><h1>安装到手机桌面</h1><button type="button" className={s.close} onClick={() => dismiss(false)} aria-label="关闭"><span aria-hidden="true">×</span></button></div> : <div className={s.title}><span className={s.brand}>发</span><div><h1>添加到主屏幕</h1><p>{ios ? 'iPhone 要这一步才能收到推送' : '从主屏幕快速打开进销存'}</p></div><button type="button" className={s.close} onClick={() => dismiss(false)} aria-label="关闭"><span aria-hidden="true">×</span></button></div>}
      {android && <div className={s.androidIntro}><span className={s.brand}>发</span><div><b>进销存</b><small>由 Chrome 安装，桌面上多一个图标</small></div></div>}
      {standalone ? <p className={s.notice}>已从主屏幕打开。到「我的 › 通知与声音」查看系统通知状态。</p>
        : !secure ? <p className={s.notice}>当前地址不是 HTTPS。请先通过 HTTPS 地址打开，才能使用后台推送。</p>
          : ios && safari ? <>
            <p className={s.summary}>从主屏幕打开：全屏、保持登录、能收推送。</p>
            <ol className={s.steps}>
              <li><b>1</b><span>点 Safari 底部的<strong>分享</strong>按钮 <span className={s.share} aria-label="分享图标">□<i>↑</i></span></span></li>
              <li><b>2</b><span>下滑，选「<strong>添加到主屏幕</strong>」</span></li>
              <li><b>3</b><span>点右上角「<strong>添加</strong>」</span></li>
            </ol>
            <p className={s.help}>添加后从主屏幕打开，到「我的 › 通知与声音」开启系统通知。</p>
          </> : android && secure ? <div className={s.androidBenefits}>
            <div><span><Icon name="scan" size={20} /></span><section><b>扫码更顺手</b><small>全屏取景，不被地址栏挤掉一截</small></section></div>
            <div><span><Icon name="bell" size={20} /></span><section><b>提醒更稳</b><small>关掉 Chrome 以后，新任务照样推送</small></section></div>
            <div><span><Icon name="back" size={20} /></span><section><b>返回键只关弹层</b><small>不会一按就退出整个系统</small></section></div>
          </div> : <p className={s.notice}>请在浏览器菜单中选择「安装应用」或「添加到主屏幕」。iPhone 请使用 Safari 打开。</p>}
      {installStatus && <p className={s.installStatus} role="status">{installStatus}</p>}
      <div className={s.actions}><button type="button" onClick={() => dismiss(true)}>{android ? '暂不' : '以后再说'}</button><button type="button" className={s.primary} onClick={() => android && secure && !standalone ? void install() : dismiss(false)}>{android && secure && !standalone ? '安装' : '知道了'}</button></div>
      <small className={s.account}>当前账号：{user?.name || '—'}</small>
    </section>
    {ios && safari && !standalone && secure && <div className={s.safariBar}><span>{window.location.hostname}</span><strong className={s.circledShare} aria-label="Safari 分享按钮">□<i>↑</i></strong></div>}
  </div>;
}

export function installGuideSnoozed() {
  try { return Number(localStorage.getItem(SNOOZE_KEY)) > Date.now(); } catch { return false; }
}
